<template>
  <master-layout id="profile-view">
    <LogoutPopup :show="showModal" @close-modal="closeModal"></LogoutPopup>
    <div
      :class="{
        'top-0': !showHeader,
        'top-navbar-height': showHeader,
      }"
    >
      <div class="lg:w-[calc(100vw-292px)]">
        <div
          id="profileBanner"
          class="relative h-profile-banner w-full"
          :class="isGICConfType ? 'bg-grey-90' : 'bg-yellow'"
        ></div>
      </div>
      <div class="flex justify-center">
        <div
          class="z-10 w-full mx-6 lg:w-[504px] lg:mx-0 mt-[-42px] inline-flex lg:flex flex-col items-left gap-[24px]"
        >
          <div>
            <div
              v-if="!showProfilePic"
              :class="[
                'flex items-center w-[84px] h-[84px] text-[#FFF] text-[40px] font-family-[\'aktiv-grotesk-medium\'] justify-center leading-loose rounded-full border-white border-[3px]',
                isGICConfType ? 'bg-orange' : 'bg-lightblue',
              ]"
            >
              {{ name.charAt(0) }}
            </div>
            <img
              v-if="showProfilePic"
              :src="profilePicture"
              class="w-[84px] h-[84px] items-left rounded-full border-white border-[3px]"
            />
          </div>
          <div class="flex flex-col w-full gap-2 text-left justify-left">
            <div
              class="w-full font-semibold text-[24px] leading-[32px] font-['Proxima Nova'] text-[#1B2A6B]"
            >
              {{ name }}
            </div>
            <div class="w-full">
              <div class="text-[16px] leading-[24px] font-aktiv-grotesk-regular">
                {{ organization }}
              </div>
              <div class="font-normal text-[16px] leading-[24px] font-aktiv-grotesk-regular">
                {{ title }}
              </div>
            </div>
            <div class="flex flex-row gap-2 items-center">
              <button
                :class="[
                  'font-aktiv-grotesk-medium flex flex-row items-center gap-[6px] px-[16px] py-[6px] rounded-[100px] text-white text-[16px] leading-[24px] text-center',
                  isGICConfType ? 'bg-orange' : 'bg-lightblue',
                ]"
                @click="showQR()"
              >
                <img
                  src="../../../public/assets/icons/qrcode-viewfinder.svg"
                  class="brightness-0 invert"
                />
                {{ $t('copy.profilePage.showQr') }}
              </button>
              <button
                :class="[
                  'font-aktiv-grotesk-medium flex items-center justify-center px-[16px] py-[8px] rounded-[100px] border-2 border-solid text-[16px] leading-[24px] text-center',
                  isGICConfType ? 'border-orange text-orange' : 'border-lightblue text-lightblue',
                ]"
                @click="editProfile()"
              >
                {{ $t('copy.profilePage.editProfile') }}
              </button>
            </div>
          </div>
          <div
            class="flex flex-col w-full p-6 rounded-[24px] bg-white border-solid border-[1px] border-grey-20"
          >
            <!-- Email -->
            <div class="mb-5">
              <div class="w-full font-aktiv-grotesk-bold font-bold text-[16px] leading-[24px] mb-1">
                {{ $t('copy.profilePage.email') }}
              </div>
              <div
                v-if="email"
                :class="[
                  'font-aktiv-grotesk-medium text-[16px] leading-[24px]',
                  isGICConfType ? 'text-orange' : 'text-lightblue',
                ]"
              >
                {{ email }}
              </div>
              <button
                v-else
                class="flex items-center gap-[8px]"
                data-event-tracking="gensapp-profile-add-email"
                @click="editSection('email')"
              >
                <img
                  src="/assets/icons/edit-pencil.svg"
                  alt=""
                  class="w-[28px] h-[28px] shrink-0"
                />
                <span
                  :class="[
                    'font-aktiv-grotesk-medium text-[16px] leading-[24px]',
                    isGICConfType ? 'text-orange' : 'text-lightblue',
                  ]"
                >
                  {{ $t('copy.profilePage.addEmail') }}
                </span>
              </button>
            </div>

            <!-- Socials -->
            <div>
              <div class="w-full font-aktiv-grotesk-bold font-bold text-[16px] leading-[24px] mb-1">
                {{ $t('copy.profilePage.socials') }}
              </div>
              <div v-if="hasSocials" class="flex flex-row items-center w-full">
                <a
                  v-if="instagram"
                  class="w-[32px] h-[32px] mr-6"
                  :href="instagramURL()"
                  target="_blank"
                >
                  <img
                    src="../../../public/assets/icons/logo-instagram.svg"
                    alt="Instagram Social"
                  />
                </a>
                <a
                  v-if="facebook"
                  class="w-[32px] h-[32px] mr-6"
                  :href="facebookURL()"
                  target="_blank"
                >
                  <img src="../../../public/assets/icons/logo-facebook.svg" alt="Facebook Social" />
                </a>
                <a v-if="x" class="w-[32px] h-[32px] mr-6" :href="xURL()" target="_blank">
                  <img src="../../../public/assets/icons/logo-twitterx.svg" alt="X Social" />
                </a>
                <a v-if="linkedin" class="w-[32px] h-[32px]" :href="linkedInURL()" target="_blank">
                  <img src="../../../public/assets/icons/logo-linkedin.svg" alt="LinkedIn Social" />
                </a>
              </div>
              <button
                v-else
                class="flex items-center gap-[8px]"
                data-event-tracking="gensapp-profile-add-socials"
                @click="editSection('socials')"
              >
                <img
                  src="/assets/icons/edit-pencil.svg"
                  alt=""
                  class="w-[28px] h-[28px] shrink-0"
                />
                <span
                  :class="[
                    'font-aktiv-grotesk-medium text-[16px] leading-[24px]',
                    isGICConfType ? 'text-orange' : 'text-lightblue',
                  ]"
                >
                  {{ $t('copy.profilePage.addSocials') }}
                </span>
              </button>
            </div>
          </div>

          <!-- Settings (moved in from the old /settings page) -->
          <div id="profile-settings-section" class="flex flex-col gap-6 w-full">
            <!-- Support -->
            <section class="rounded-3xl border border-grey-20 overflow-hidden">
              <div class="bg-white px-6 py-3 border-b border-grey-20">
                <p class="font-aktiv-grotesk-bold font-bold text-[16px] leading-6 text-black">
                  {{ $t('copy.settingsPage.support.title') }}
                </p>
              </div>
              <button
                v-for="item in supportItems"
                :key="item.key"
                class="bg-white w-full flex items-center justify-between gap-4 px-6 py-3 border-b border-grey-20 active:bg-grey-5 transition-colors duration-100"
                :data-event-tracking="item.dataEventTracking"
                :data-test-id="item.dataTestId"
                @click="handleItemClick(item)"
              >
                <img :src="item.icon" alt="" class="shrink-0 w-6 h-6 object-contain" />
                <span
                  class="font-aktiv-grotesk text-[16px] leading-6 text-[#1b2a6b] flex-1 text-left"
                >
                  {{ item.label }}
                </span>
                <img src="/assets/icons/chevron-right-grey.svg" alt="" class="shrink-0" />
              </button>
            </section>

            <!-- Conference Selector — only when the user has >1 conference type; sits above Logout -->
            <section
              v-if="showConferenceSelector"
              class="rounded-3xl border border-grey-20 overflow-hidden"
            >
              <button
                class="bg-white w-full flex items-center justify-between gap-4 px-6 py-3 active:bg-grey-5 transition-colors duration-100"
                data-event-tracking="gensapp-settings-conferenceselector"
                data-test-id="gensapp-settings-conferenceselector"
                @click="
                  handleItemClick({
                    key: 'conferenceSelector',
                    type: 'route',
                    value: conferenceRoute,
                  })
                "
              >
                <img
                  src="/assets/icons/calendar-grey.svg"
                  alt=""
                  class="shrink-0 w-6 h-6 object-contain"
                />
                <span
                  class="font-aktiv-grotesk text-[16px] leading-6 text-[#1b2a6b] flex-1 text-left"
                >
                  {{ $t('copy.settingsPage.general.conferenceSelector') }}
                </span>
                <img src="/assets/icons/chevron-right-grey.svg" alt="" class="shrink-0" />
              </button>
            </section>

            <!-- Logout -->
            <button
              class="bg-white w-full flex items-center gap-3 px-6 py-3 rounded-3xl border border-grey-20 active:bg-grey-5 transition-colors duration-100"
              data-event-tracking="gensapp-settings-logout"
              data-test-id="gensapp-settings-logout"
              @click="openModal()"
            >
              <img src="/assets/icons/logout-icon-grey.svg" alt="" class="shrink-0 w-6 h-6" />
              <span class="font-aktiv-grotesk-medium text-[16px] leading-6 text-orange">{{
                $t('copy.settingsPage.logout')
              }}</span>
            </button>

            <!-- Build version (native only) — extra bottom padding so it scrolls clear of the floating nav pill -->
            <p v-if="buildVersion" class="text-grey-70 text-sm text-center pb-12 lg:pb-2">
              {{ buildVersion }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </master-layout>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import { isNativeNotWeb } from '@/utils/platform-check';
import { FirebaseAnalytics } from '@/utils/firebase-analytics-wrapper';
import platformMixin from '@/mixins/platformMixin';
import {
  getCurrentConferenceType,
  hasMultipleConferenceTypes,
  isGPCRPage,
} from '@/utils/conference-type';
import LogoutPopup from '@/components/HomePage/LogoutPopup.vue';

export default {
  name: 'Social',
  components: { LogoutPopup },
  mixins: [platformMixin],
  data() {
    return {
      showProfilePic: false,
      isSamsung: false,
      buildVersion: '',
      isGICConfType: false,
      showModal: false,
      mailtoLink: 'mailto:hello@generationsmvmt.com?subject=GenS%20App%20Enquiry',
    };
  },
  computed: {
    ...mapState('profile', ['confYear', 'conferences']),
    ...mapState('social', [
      'name',
      'organization',
      'title',
      'email',
      'instagram',
      'facebook',
      'x',
      'linkedin',
      'profilePicture',
    ]),
    ...mapGetters('appVersion', ['getCurrentVersion']),
    showHeader() {
      return this.$route.path !== '/home';
    },
    hasSocials() {
      return !!(this.instagram || this.facebook || this.x || this.linkedin);
    },
    showConferenceSelector() {
      return hasMultipleConferenceTypes(this.confYear, this.conferences);
    },
    conferenceRoute() {
      if (this.isGICConfType) return '/gic/conference';
      if (isGPCRPage()) return '/gpcr/conference';
      return '/conference';
    },
    supportItems() {
      return [
        {
          key: 'enquiry',
          icon: '/assets/icons/enquiry-grey.svg',
          label: this.$t('copy.settingsPage.support.enquiry'),
          type: 'enquiry',
          dataEventTracking: 'gensapp-settings-enquiry',
          dataTestId: 'gensapp-settings-enquiry',
        },
        {
          key: 'privacyPolicy',
          icon: '/assets/icons/document-icon-grey.svg',
          label: this.$t('copy.settingsPage.support.privacyPolicy'),
          type: 'route',
          value: '/privacy-policy',
          dataEventTracking: 'gensapp-settings-privacypolicy',
          dataTestId: 'gensapp-settings-privacypolicy',
        },
        {
          key: 'termsOfUse',
          icon: '/assets/icons/document-icon-grey.svg',
          label: this.$t('copy.settingsPage.support.termsOfUse'),
          type: 'route',
          value: '/terms-of-use',
          dataEventTracking: 'gensapp-settings-termsofuse',
          dataTestId: 'gensapp-settings-termsofuse',
        },
      ];
    },
  },

  async mounted() {
    if (navigator.userAgent.match(/samsung/i)) {
      this.isSamsung = true;
    }
    if (isNativeNotWeb()) {
      this.buildVersion = 'VERSION ' + this.getCurrentVersion;
    }
  },
  async ionViewWillEnter() {
    this.isGICConfType = getCurrentConferenceType(this.confYear, this.conferences) === 'GIC';
    await this.$store.dispatch('social/fetchSocialProfile');
    if (this.profilePicture) {
      this.showProfilePic = true;
    }
    FirebaseAnalytics.setScreenName({
      screenName: 'profile - view',
      nameOverride: 'ProfilePage - View',
    });
  },
  methods: {
    back() {
      this.$router.push('/home');
    },
    editProfile() {
      this.$router.push('/profile/edit');
    },
    editSection(section) {
      this.$router.push({ path: '/profile/edit', query: { section } });
    },
    handleItemClick(item) {
      if (item.type === 'route') {
        if (item.key === 'conferenceSelector') {
          this.$router.push({ path: item.value, query: { from: 'settings' } });
        } else {
          this.$router.push(item.value);
        }
      } else if (item.type === 'enquiry') {
        window.location.href = this.mailtoLink;
      }
    },
    openModal() {
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
    },
    showQR() {
      this.$router.push('/qr-code');
    },
    instagramURL() {
      return 'https://www.instagram.com/' + this.instagram; // Remove as string when store has been re-adjusted
    },
    facebookURL() {
      return 'https://www.facebook.com/' + this.facebook;
    },
    xURL() {
      return 'https://www.x.com/' + this.x;
    },
    linkedInURL() {
      return 'https://www.linkedin.com/in/' + this.linkedin;
    },
  },
};
</script>
<style scoped>
.view-qr-code-shadow {
  box-shadow: 0px 1px 10px 0px #0000001a;
}
@media (min-width: 769px) {
  #profileBanner {
    background-size: 100% 100%; /* Stretch image to cover entire area */
    background-repeat: no-repeat; /* Prevent image from repeating */
    background-position: center; /* Center the background image */
  }
}

@media (max-width: 768px) {
  #profileBanner {
    background-size: 100% 100%; /* Stretch image to cover entire area */
    background-repeat: no-repeat; /* Prevent image from repeating */
    background-position: center; /* Center the background image */
  }
}
</style>
